import { useMutation } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { Banner, Button, Card } from "@magarac/ui";
import { api } from "../lib/api";
import { messageFor } from "../lib/errors";

export const Route = createFileRoute("/pay/$token/test-checkout")({
  validateSearch: (s: Record<string, unknown>): { session: string } => ({
    session: typeof s.session === "string" ? s.session : "",
  }),
  component: TestCheckoutPage,
});

/**
 * Stands in for Stripe Checkout until Stripe Connect is set up (never available in production).
 * Completing it sends a Stripe-shaped webhook through the real intake, queue, and consumer.
 */
function TestCheckoutPage() {
  const { token } = Route.useParams();
  const { session } = Route.useSearch();
  const navigate = useNavigate();
  const complete = useMutation({
    mutationFn: () =>
      api.post(
        `/public/pay/${token}/test-complete`,
        { session },
        z.object({ ok: z.literal(true) }),
      ),
    onSuccess: () => navigate({ to: "/pay/$token", params: { token }, search: { paid: true } }),
  });
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center p-4">
      <Card>
        <h1 className="mb-2 text-xl font-semibold">Test payment</h1>
        <Banner tone="warn">
          This is a test checkout. No card is charged. Real card payments start once the business
          connects Stripe.
        </Banner>
        {complete.error ? <Banner tone="danger">{messageFor(complete.error)}</Banner> : null}
        <div className="grid gap-2">
          <Button busy={complete.isPending} onClick={() => complete.mutate()}>
            Complete test payment
          </Button>
          <Button
            variant="secondary"
            onClick={() => navigate({ to: "/pay/$token", params: { token }, search: {} })}
          >
            Cancel
          </Button>
        </div>
      </Card>
    </main>
  );
}
